import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ProtectedImage from "../../components/ProtectedImage";
import FormDrawer from "../../components/layout/FormDrawer";
import { BRAND, primaryButtonSx } from "../../components/auth/authTheme";

const DRAWER_WIDTH = { xs: "100%", sm: 600, md: 820 };

const DetailItem = ({ icon, label, value, fullWidth = false }) => (
  <Box
    sx={{
      display: "flex",
      gap: 2,
      p: 2,
      borderRadius: "12px",
      border: `1px solid ${BRAND.border}`,
      bgcolor: "#fff",
      gridColumn: fullWidth ? "1 / -1" : undefined,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        bgcolor: BRAND.orangeLight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: BRAND.orange,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: BRAND.muted,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontSize: "0.7rem",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: value ? BRAND.text : BRAND.muted,
          fontWeight: value ? 500 : 400,
          mt: 0.25,
          wordBreak: "break-word",
        }}
      >
        {value || "Not provided"}
      </Typography>
    </Box>
  </Box>
);

const DetailGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
      gap: 2,
    }}
  >
    {children}
  </Box>
);

const PermissionBadge = ({ checked, label }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      bgcolor: checked ? BRAND.orangeLight : BRAND.bg,
      color: checked ? BRAND.orange : BRAND.muted,
      fontWeight: 600,
      fontSize: "0.7rem",
      border: `1px solid ${checked ? BRAND.orange : BRAND.border}`,
      textTransform: "capitalize",
    }}
  />
);

const PERMISSION_KEYS = [
  "view",
  "edit",
  "create",
  "delete",
  "approve",
  "reject",
  "assign",
  "unassign",
  "export",
  "import",
  "generate_report",
  "archive",
  "restore",
];

const EMPLOYMENT_TYPE_LABELS = {
  FT: "Full Time",
  PT: "Part Time",
  CT: "Contract",
  IN: "Intern",
};

const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const formatAttendanceType = (str) => {
  if (!str) return null;
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

const EmployeeProfileView = ({ show, handleClose, selectedEmployee }) => {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!show) setActiveTab(0);
  }, [show]);

  if (!selectedEmployee) return null;

  const profile = selectedEmployee.profile_data || {};
  const salary = selectedEmployee.employee_salary || {};
  const leave = selectedEmployee.leave_balance || {};
  const fullName =
    `${selectedEmployee.first_name || ""} ${selectedEmployee.last_name || ""}`.trim();
  const initials =
    `${selectedEmployee.first_name?.[0] || ""}${selectedEmployee.last_name?.[0] || ""}`.toUpperCase();
  const reportingManager = profile.reporting_manager
    ? `${profile.reporting_manager.first_name || ""} ${profile.reporting_manager.last_name || ""}`.trim()
    : null;
  const leaveManagers = leave.leave_approval_managers?.length
    ? leave.leave_approval_managers
        .map((m) => `${m.first_name || ""} ${m.last_name || ""}`.trim())
        .filter(Boolean)
        .join(", ")
    : null;
  const deductions = salary.deductions
    ? Object.entries(salary.deductions)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ")
    : null;
  const challans = Array.isArray(salary.challans) ? salary.challans : [];
  const permissions = selectedEmployee.permissions?.permissions || [];

  return (
    <FormDrawer
      open={show}
      onClose={handleClose}
      width={DRAWER_WIDTH}
      title={fullName || "Employee"}
      subtitle={
        selectedEmployee.employee_id
          ? `Employee ID: ${selectedEmployee.employee_id}`
          : "Employee profile"
      }
      footer={
        <Button
          variant="contained"
          color="primary"
          onClick={handleClose}
          sx={{ ...primaryButtonSx, px: 4 }}
        >
          Close
        </Button>
      }
    >
      <Box
        sx={{
          textAlign: "center",
          py: 3,
          px: 2,
          mb: 2.5,
          borderRadius: "16px",
          background: `linear-gradient(135deg, ${BRAND.navy} 0%, #243b5e 60%, rgba(255,106,0,0.25) 100%)`,
        }}
      >
        {profile.profile_image ? (
          <Box
            sx={{
              width: 88,
              height: 88,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              overflow: "hidden",
              border: "3px solid rgba(255,255,255,0.3)",
            }}
          >
            <ProtectedImage
              path={profile.profile_image}
              alt={fullName}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
        ) : (
          <Avatar
            sx={{
              width: 88,
              height: 88,
              mx: "auto",
              mb: 2,
              bgcolor: BRAND.orange,
              fontSize: "1.75rem",
              fontWeight: 700,
            }}
          >
            {initials || "?"}
          </Avatar>
        )}

        <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
          {fullName}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
          {profile.designation_name || "Designation not set"}
        </Typography>
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Chip
            label={selectedEmployee.is_active ? "Active" : "Inactive"}
            size="small"
            sx={{
              bgcolor: selectedEmployee.is_active
                ? "rgba(74,222,128,0.2)"
                : "rgba(255,255,255,0.15)",
              color: selectedEmployee.is_active ? "#4ADE80" : "rgba(255,255,255,0.8)",
              fontWeight: 600,
            }}
          />
          {selectedEmployee.company?.company_name && (
            <Chip
              label={selectedEmployee.company.company_name}
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.12)",
                color: "#fff",
                fontWeight: 500,
              }}
            />
          )}
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_e, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2.5,
          borderBottom: `1px solid ${BRAND.border}`,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.8125rem",
            minHeight: 44,
            color: BRAND.muted,
          },
          "& .MuiTab-root.Mui-selected": { color: BRAND.navy },
          "& .MuiTabs-indicator": {
            bgcolor: BRAND.orange,
            height: 3,
            borderRadius: "3px 3px 0 0",
          },
        }}
      >
        <Tab label="Basic" />
        <Tab label="Employment" />
        <Tab label="Salary" />
        <Tab label="Leave" />
        <Tab label="Challans" />
        <Tab label="Permissions" />
      </Tabs>

      {activeTab === 0 && (
        <DetailGrid>
          <DetailItem
            icon={<EmailOutlinedIcon fontSize="small" />}
            label="Email"
            value={selectedEmployee.email}
          />
          <DetailItem
            icon={<PhoneOutlinedIcon fontSize="small" />}
            label="Mobile Number"
            value={selectedEmployee.mobile_number}
          />
          <DetailItem
            icon={<PersonOutlineIcon fontSize="small" />}
            label="Gender"
            value={capitalizeFirstLetter(selectedEmployee.gender)}
          />
          <DetailItem
            icon={<PublicOutlinedIcon fontSize="small" />}
            label="Nationality"
            value={selectedEmployee.nationality}
          />
        </DetailGrid>
      )}

      {activeTab === 1 && (
        <DetailGrid>
          <DetailItem
            icon={<BusinessOutlinedIcon fontSize="small" />}
            label="Company"
            value={selectedEmployee.company?.company_name}
          />
          <DetailItem
            icon={<WorkOutlineIcon fontSize="small" />}
            label="Department"
            value={profile.department_name}
          />
          <DetailItem
            icon={<WorkOutlineIcon fontSize="small" />}
            label="Designation"
            value={profile.designation_name}
          />
          <DetailItem
            icon={<CalendarTodayOutlinedIcon fontSize="small" />}
            label="Joining Date"
            value={profile.joining_date}
          />
          <DetailItem
            icon={<WorkOutlineIcon fontSize="small" />}
            label="Employment Type"
            value={
              EMPLOYMENT_TYPE_LABELS[profile.employment_type] ||
              profile.employment_type
            }
          />
          <DetailItem
            icon={<LocationOnOutlinedIcon fontSize="small" />}
            label="Work Location"
            value={profile.work_location}
          />
          <DetailItem
            icon={<SupervisorAccountOutlinedIcon fontSize="small" />}
            label="Reporting Manager"
            value={reportingManager}
          />
          <DetailItem
            icon={<PersonOutlineIcon fontSize="small" />}
            label="Employee Status"
            value={profile.employee_status}
          />
          <DetailItem
            icon={<DescriptionOutlinedIcon fontSize="small" />}
            label="Job Description"
            value={profile.job_description}
            fullWidth
          />
          <DetailItem
            icon={<AccessTimeOutlinedIcon fontSize="small" />}
            label="Shift Timing"
            value={profile.shift_timing}
          />
          <DetailItem
            icon={<EventBusyOutlinedIcon fontSize="small" />}
            label="Weekly Off"
            value={profile.weekly_off}
          />
          <DetailItem
            icon={<AccessTimeOutlinedIcon fontSize="small" />}
            label="Working Hours"
            value={displayValue(profile.working_hours)}
          />
        </DetailGrid>
      )}

      {activeTab === 2 && (
        <DetailGrid>
          <DetailItem
            icon={<AttachMoneyOutlinedIcon fontSize="small" />}
            label="Basic Salary"
            value={displayValue(salary.basic_salary)}
          />
          <DetailItem
            icon={<AttachMoneyOutlinedIcon fontSize="small" />}
            label="HRA"
            value={displayValue(salary.hra)}
          />
          <DetailItem
            icon={<AttachMoneyOutlinedIcon fontSize="small" />}
            label="Bonus"
            value={displayValue(salary.bonus)}
          />
          <DetailItem
            icon={<ReceiptLongOutlinedIcon fontSize="small" />}
            label="Deductions"
            value={deductions}
            fullWidth
          />
          <DetailItem
            icon={<AccountBalanceOutlinedIcon fontSize="small" />}
            label="Bank Name"
            value={salary.bank_name}
          />
          <DetailItem
            icon={<AccountBalanceOutlinedIcon fontSize="small" />}
            label="Account Number"
            value={salary.account_number}
          />
          <DetailItem
            icon={<AccountBalanceOutlinedIcon fontSize="small" />}
            label="IFSC Code"
            value={salary.ifsc_code}
          />
          <DetailItem
            icon={<ReceiptLongOutlinedIcon fontSize="small" />}
            label="PAN / Tax ID"
            value={salary.pan_number}
          />
          <DetailItem
            icon={<ReceiptLongOutlinedIcon fontSize="small" />}
            label="UAN / PF Number"
            value={salary.pf_number}
          />
          <DetailItem
            icon={<ReceiptLongOutlinedIcon fontSize="small" />}
            label="ESI Number"
            value={salary.esi_number}
          />
        </DetailGrid>
      )}

      {activeTab === 3 && (
        <DetailGrid>
          <DetailItem
            icon={<SupervisorAccountOutlinedIcon fontSize="small" />}
            label="Leave Approval Managers"
            value={leaveManagers}
            fullWidth
          />
          <DetailItem
            icon={<AccessTimeOutlinedIcon fontSize="small" />}
            label="Attendance Type"
            value={formatAttendanceType(leave.attendance_type)}
          />
          <DetailItem
            icon={<CalendarTodayOutlinedIcon fontSize="small" />}
            label="Leave Year"
            value={displayValue(leave.leave_year)}
          />
          <DetailItem
            icon={<BeachAccessOutlinedIcon fontSize="small" />}
            label="Overtime Eligible"
            value={
              leave.overtime_eligible === true
                ? "Yes"
                : leave.overtime_eligible === false
                  ? "No"
                  : null
            }
          />
          <DetailItem
            icon={<BeachAccessOutlinedIcon fontSize="small" />}
            label="Paid Leave Balance"
            value={displayValue(leave.paid_leave_balance)}
          />
          <DetailItem
            icon={<BeachAccessOutlinedIcon fontSize="small" />}
            label="Unpaid Leave Balance"
            value={displayValue(leave.unpaid_leave_balance)}
          />
        </DetailGrid>
      )}

      {activeTab === 4 && (
        <Box>
          {challans.length === 0 ? (
            <Typography sx={{ color: BRAND.muted, textAlign: "center", py: 4 }}>
              No challans assigned to this employee
            </Typography>
          ) : (
            <Box
              sx={{
                border: `1px solid ${BRAND.border}`,
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: BRAND.bg }}>
                    {["#", "Challan Name", "Amount", "Percentage"].map(
                      (head) => (
                        <TableCell
                          key={head}
                          sx={{
                            fontWeight: 700,
                            color: BRAND.navy,
                            fontSize: "0.8125rem",
                            borderColor: BRAND.border,
                          }}
                        >
                          {head}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {challans.map((challan, index) => (
                    <TableRow key={challan.id || index} hover>
                      <TableCell sx={{ borderColor: BRAND.border }}>
                        {index + 1}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderColor: BRAND.border,
                          fontWeight: 500,
                          color: BRAND.orange,
                        }}
                      >
                        {challan.name}
                      </TableCell>
                      <TableCell sx={{ borderColor: BRAND.border }}>
                        {challan.amount != null ? challan.amount : "—"}
                      </TableCell>
                      <TableCell sx={{ borderColor: BRAND.border }}>
                        {challan.percentage != null
                          ? `${challan.percentage}%`
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      )}

      {activeTab === 5 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {permissions.length === 0 ? (
            <Typography sx={{ color: BRAND.muted, textAlign: "center", py: 4 }}>
              No permissions configured
            </Typography>
          ) : (
            permissions.map((perm) => (
              <Box
                key={perm.module}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: `1px solid ${BRAND.border}`,
                  bgcolor: perm.enabled ? "#fff" : BRAND.bg,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: BRAND.navy }}
                  >
                    {perm.moduleLabel || perm.module}
                  </Typography>
                  <Chip
                    label={perm.enabled ? "Enabled" : "Disabled"}
                    size="small"
                    sx={{
                      bgcolor: perm.enabled ? BRAND.orangeLight : BRAND.bg,
                      color: perm.enabled ? BRAND.orange : BRAND.muted,
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {PERMISSION_KEYS.map(
                    (key) =>
                      perm[key] !== undefined && (
                        <PermissionBadge
                          key={key}
                          checked={Boolean(perm[key])}
                          label={key.replace(/_/g, " ")}
                        />
                      )
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}
    </FormDrawer>
  );
};

export default EmployeeProfileView;
